import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Download,
  Database,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  History,
  Search,
} from 'lucide-react';
import {
  listExportableDatabases,
  startExport,
  getExportStatus,
  getExportHistory,
} from '../../services/databaseExportApi';

const POLL_INTERVAL_MS = 5000;

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const styles = {
    succeeded:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    failed:     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    inProgress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    pending:    'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] ?? styles.pending}`}>
      {status === 'inProgress' && <Loader2 className="w-3 h-3 animate-spin" />}
      {status}
    </span>
  );
};

// ─── Confirm modal ────────────────────────────────────────────────────────────

const ConfirmModal = ({ selected, onConfirm, onCancel, loading }) => {
  const [checked, setChecked] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Confirm export</h2>
        </div>

        <div className="space-y-3 mb-5 text-sm text-gray-700 dark:text-gray-300">
          <p>
            About to export <span className="font-semibold">{selected.length}</span>{' '}
            production {selected.length === 1 ? 'database' : 'databases'} to BACPAC.
            This runs against{' '}
            <code className="font-mono text-xs">innova-production-sqlserver</code> and
            can take many minutes per DB.
          </p>
          <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 max-h-40 overflow-auto">
            {selected.map((db) => (
              <p key={db} className="font-mono text-xs text-gray-700 dark:text-gray-300">{db}</p>
            ))}
          </div>
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer mb-5">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            I understand this exports production data and that the BACPACs will land in
            the configured storage container.
          </span>
        </label>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!checked || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Start export
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── History block ────────────────────────────────────────────────────────────

const HistoryBlock = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getExportHistory(10)
      .then(setHistory)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-slate-500 py-4">
      <Loader2 className="w-4 h-4 animate-spin" />
      Loading history…
    </div>
  );

  if (history.length === 0) return (
    <p className="text-sm text-gray-400 dark:text-slate-500 italic py-4">
      No previous export sessions.
    </p>
  );

  return (
    <div className="space-y-2">
      {history.map((s) => {
        const okCount   = s.items?.filter((i) => i.status === 'succeeded').length ?? 0;
        const failCount = s.items?.filter((i) => i.status === 'failed').length ?? 0;
        const total     = s.items?.length ?? 0;
        return (
          <div
            key={s.sessionId}
            className="flex items-center justify-between gap-3 px-3 py-2.5 bg-gray-50 dark:bg-slate-700/50 rounded-lg"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={s.overallStatus} />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  {okCount}/{total} succeeded
                  {failCount > 0 && (
                    <span className="text-red-600 dark:text-red-400"> · {failCount} failed</span>
                  )}
                </span>
                {s.triggeredBy && (
                  <span className="text-xs text-gray-400 dark:text-slate-500">by {s.triggeredBy}</span>
                )}
              </div>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                {new Date(s.startedAt).toLocaleString()}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const DatabaseExport = () => {
  const [phase, setPhase] = useState('idle');
  // idle → loading → select → confirming → running → done

  const [databases, setDatabases] = useState([]);
  const [selected, setSelected]   = useState(new Set());
  const [filter, setFilter]       = useState('');
  const [session, setSession]     = useState(null);
  const [error, setError]         = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const pollRef = useRef(null);

  useEffect(() => {
    if (session?.overallStatus === 'succeeded' || session?.overallStatus === 'failed') {
      clearInterval(pollRef.current);
    }
  }, [session?.overallStatus]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const startPolling = (sessionId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await getExportStatus(sessionId);
        setSession(status);
      } catch {
        // ignore transient poll errors
      }
    }, POLL_INTERVAL_MS);
  };

  const loadDatabases = async () => {
    setPhase('loading');
    setError(null);
    try {
      const dbs = await listExportableDatabases();
      setDatabases(dbs);
      setPhase('select');
    } catch (err) {
      setError(err.message || 'Failed to load databases.');
      setPhase('idle');
    }
  };

  const filteredDatabases = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return databases;
    return databases.filter((d) => d.name.toLowerCase().includes(q));
  }, [databases, filter]);

  const toggleOne = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected(new Set(filteredDatabases.map((d) => d.name)));
  };

  const clearSelection = () => setSelected(new Set());

  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      const { sessionId } = await startExport(Array.from(selected));
      setSession({
        sessionId,
        overallStatus: 'inProgress',
        startedAt: new Date().toISOString(),
        items: Array.from(selected).map((name) => ({ databaseName: name, status: 'pending' })),
      });
      setPhase('running');
      startPolling(sessionId);
    } catch (err) {
      setError(err.message || 'Failed to start export.');
      setPhase('select');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleReset = () => {
    clearInterval(pollRef.current);
    setPhase('idle');
    setDatabases([]);
    setSelected(new Set());
    setFilter('');
    setSession(null);
    setError(null);
  };

  const isComplete = session?.overallStatus === 'succeeded';
  const isFailed   = session?.overallStatus === 'failed';

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Download className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Database Export</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Export production databases on{' '}
            <code className="font-mono text-xs">innova-production-sqlserver</code> to BACPAC.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
          <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {phase === 'idle' && (
        <>
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 text-center">
            <Database className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
              Load the database list to begin.
            </p>
            <button
              onClick={loadDatabases}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Load databases
            </button>
          </div>

          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-4 h-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Recent sessions</h2>
            </div>
            <HistoryBlock />
          </div>
        </>
      )}

      {phase === 'loading' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
          <span className="text-sm text-gray-500 dark:text-slate-400">Loading databases…</span>
        </div>
      )}

      {phase === 'select' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter databases…"
                className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={selectAllVisible}
              className="px-3 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700 rounded-lg"
            >
              Select all ({filteredDatabases.length})
            </button>
            <button
              onClick={clearSelection}
              className="px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg"
            >
              Clear
            </button>
          </div>

          <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-700/50 text-xs uppercase text-gray-500 dark:text-slate-400">
                <tr>
                  <th className="w-10 px-3 py-2"></th>
                  <th className="text-left px-3 py-2">Database</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Size (MB)</th>
                  <th className="text-left px-3 py-2">Elastic Pool</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {filteredDatabases.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-xs text-gray-400 dark:text-slate-500 italic">
                      No databases match the filter.
                    </td>
                  </tr>
                ) : filteredDatabases.map((db) => (
                  <tr
                    key={db.name}
                    className="hover:bg-gray-50 dark:hover:bg-slate-700/40 cursor-pointer"
                    onClick={() => toggleOne(db.name)}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(db.name)}
                        onChange={() => toggleOne(db.name)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-900 dark:text-gray-100">{db.name}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400">{db.status}</td>
                    <td className="px-3 py-2 text-xs text-right text-gray-500 dark:text-slate-400">{db.sizeMb ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400">{db.elasticPool ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              Back
            </button>
            <button
              onClick={() => setPhase('confirming')}
              disabled={selected.size === 0}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Export {selected.size > 0 ? `(${selected.size})` : ''}
            </button>
          </div>
        </div>
      )}

      {phase === 'confirming' && (
        <ConfirmModal
          selected={Array.from(selected)}
          loading={confirmLoading}
          onConfirm={handleConfirm}
          onCancel={() => setPhase('select')}
        />
      )}

      {(phase === 'running' || isComplete || isFailed) && session && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Export progress</h2>
              <p className="text-xs text-gray-400 dark:text-slate-500 font-mono">{session.sessionId}</p>
            </div>
            <StatusBadge status={session.overallStatus} />
          </div>

          <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-700/50 text-xs uppercase text-gray-500 dark:text-slate-400">
                <tr>
                  <th className="text-left px-3 py-2">Database</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">BACPAC</th>
                  <th className="text-left px-3 py-2">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {session.items?.map((item) => (
                  <tr key={item.databaseName}>
                    <td className="px-3 py-2 font-mono text-xs text-gray-900 dark:text-gray-100">{item.databaseName}</td>
                    <td className="px-3 py-2"><StatusBadge status={item.status} /></td>
                    <td className="px-3 py-2 text-xs">
                      {item.bacpacUri ? (
                        <a
                          href={item.bacpacUri}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-blue-600 dark:text-blue-400 hover:underline break-all"
                        >
                          {item.bacpacUri.split('/').pop()}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400">
                      {item.error || (item.completedAt && item.startedAt
                        ? `${Math.round((new Date(item.completedAt) - new Date(item.startedAt)) / 1000)}s`
                        : '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isComplete && (
            <div className="flex items-center gap-2.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-700 dark:text-green-300">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>All exports succeeded.</span>
            </div>
          )}
          {isFailed && (
            <div className="flex items-start gap-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>One or more exports failed. See the detail column.</span>
            </div>
          )}

          <div className="flex items-center gap-4">
            {phase === 'running' && (
              <button
                onClick={async () => {
                  setSelected(new Set());
                  setError(null);
                  setPhase('loading');
                  try {
                    const dbs = await listExportableDatabases();
                    setDatabases(dbs);
                    setPhase('select');
                  } catch (err) {
                    setError(err.message || 'Failed to load databases.');
                    setPhase('idle');
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Start another export
              </button>
            )}
            {(isComplete || isFailed) && (
              <button onClick={handleReset} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                Start over
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseExport;
